import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useContent } from "@/context/ContentContext";

const MaskedLine = ({ children, delay, start, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={start ? { y: 0 } : {}}
      transition={{ duration: 1.1, delay, ease: [0.33, 1, 0.68, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ start }) {
  const content = useContent();
  const hero = content.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yCards = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 20 });

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-obsidian bg-grid-faint pt-24"
    >
      <motion.div
        style={{ y: yBg }}
        className="pointer-events-none absolute -right-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-gold/10 blur-[140px]"
      />
      <motion.div
        style={{ y: yBg }}
        className="pointer-events-none absolute -left-40 bottom-0 h-[26rem] w-[26rem] rounded-full bg-gold/5 blur-[120px]"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 py-20 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
        <motion.div style={{ opacity }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3"
          >
            <Sparkles className="h-4 w-4 text-gold" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">
              {hero.overline}
            </span>
          </motion.div>

          <h1 className="font-display text-[13vw] font-extrabold leading-[0.98] tracking-tight text-zinc-100 sm:text-7xl lg:text-[5.6rem]">
            <MaskedLine start={start} delay={0.25}>{hero.line1}</MaskedLine>
            <MaskedLine start={start} delay={0.38}>
              <span className="text-gold">{hero.line2}</span>
            </MaskedLine>
            <MaskedLine start={start} delay={0.51}>{hero.line3}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToId("#hizmetler")}
              className="group flex items-center gap-2 rounded-full bg-gold px-7 py-4 font-display text-base font-bold text-obsidian transition-transform duration-300 hover:scale-105"
            >
              Hizmetleri Keşfet
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("#iletisim")}
              className="group flex items-center gap-2 rounded-full border border-gold/30 px-7 py-4 font-display text-base font-bold text-zinc-100 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Teklif Al
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={start ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 flex items-center gap-8 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500"
          >
            <span><span className="text-gold">18</span> Hizmet</span>
            <span className="h-3 w-px bg-gold/30" />
            <span><span className="text-gold">4</span> Disiplin</span>
            <span className="h-3 w-px bg-gold/30" />
            <span><span className="text-gold">Tek</span> Çatı</span>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: yCards, rotateX, rotateY, transformPerspective: 1200 }}
          className="relative hidden h-[34rem] lg:block"
        >
          <motion.div
            initial={{ opacity: 0, y: 80, rotate: -4 }}
            animate={start ? { opacity: 1, y: 0, rotate: -6 } : {}}
            transition={{ duration: 1.1, delay: 0.7, ease: [0.33, 1, 0.68, 1] }}
            className="gold-glow absolute left-0 top-6 w-64 overflow-hidden rounded-2xl border border-gold/25"
          >
            <img src={hero.images.main} alt="Tabela ve LED tabela uygulaması" className="h-80 w-full object-cover" />
            <div className="glass-panel absolute bottom-3 left-3 right-3 rounded-xl px-4 py-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Tabela & LED</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 80, rotate: 6 }}
            animate={start ? { opacity: 1, y: 0, rotate: 4 } : {}}
            transition={{ duration: 1.1, delay: 0.9, ease: [0.33, 1, 0.68, 1] }}
            className="gold-glow absolute right-0 top-40 w-56 overflow-hidden rounded-2xl border border-gold/25"
          >
            <img src={hero.images.print} alt="Altın yaldızlı matbaa baskısı" className="h-64 w-full object-cover" />
            <div className="glass-panel absolute bottom-3 left-3 right-3 rounded-xl px-4 py-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Matbaa</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 80, rotate: 10 }}
            animate={start ? { opacity: 1, y: 0, rotate: 9 } : {}}
            transition={{ duration: 1.1, delay: 1.1, ease: [0.33, 1, 0.68, 1] }}
            className="gold-glow absolute bottom-0 left-20 w-52 overflow-hidden rounded-2xl border border-gold/25"
          >
            <img src={hero.images.video} alt="Profesyonel video prodüksiyon seti" className="h-48 w-full object-cover" />
            <div className="glass-panel absolute bottom-3 left-3 right-3 rounded-xl px-4 py-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Prodüksiyon</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={start ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.35, type: "spring" }}
            className="absolute -top-4 right-8 flex h-28 w-28 items-center justify-center"
          >
            <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
              <defs>
                <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <text className="fill-gold font-mono text-[9.5px] uppercase tracking-[0.24em]">
                <textPath href="#circlePath">REYA DESING • MARKA MİMARLIĞI •</textPath>
              </text>
            </svg>
            <span className="absolute font-display text-2xl font-extrabold text-gold">R</span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 sm:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-zinc-500"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Kaydır</span>
          <ArrowDown className="h-4 w-4 text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
